import { useEffect, useState } from "react";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

// Safe Delete danger area (Lot C2). Secondary/discreet. Shows eligibility + tenant-safe blockers.
export function PartyDeleteZone({ stableId, partyType, partyId, name, onArchive, onDeleted }) {
  const base = partyType === "organization" ? "organizations" : "persons";
  const noun = partyType === "organization" ? "organizzazione" : "persona";
  const [elig, setElig] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!stableId || !partyId) return;
    let alive = true;
    setElig(null);
    api.get(`/stables/${stableId}/${base}/${partyId}/delete-eligibility`)
      .then((r) => { if (alive) setElig(r.data); })
      .catch(() => { if (alive) setElig({ can_delete: false, blockers: [], reason: null, error: true }); });
    return () => { alive = false; };
  }, [stableId, base, partyId]);

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/stables/${stableId}/${base}/${partyId}`);
      toast.success(partyType === "organization" ? "Organizzazione eliminata" : "Persona eliminata");
      setConfirm(false);
      onDeleted && onDeleted();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d?.reason || "Impossibile eliminare") : (d || "Errore"));
      if (typeof d === "object") setElig({ ...d, can_delete: false });
      setConfirm(false);
    } finally { setBusy(false); }
  };

  if (elig === null || elig.error) return null;

  return (
    <div className="mt-6 border-t border-[#E5DEC9] pt-4" data-testid="party-delete-zone">
      {elig.can_delete ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-xs text-[#9AA39C]">
            Questa {noun} non è utilizzata in Equiro e può essere eliminata definitivamente.
          </p>
          <button data-testid="party-delete-open" onClick={() => setConfirm(true)}
            className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 self-start">
            <Trash2 className="h-4 w-4" /> Elimina definitivamente
          </button>
        </div>
      ) : (
        <div className="rounded-lg bg-[#FBF6EE] border border-[#E5DEC9] p-3" data-testid="party-delete-blocked">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[#8C6D4F]" />
            <div className="min-w-0">
              <p className="text-sm text-[#5F4626]">{elig.reason || `Questa ${noun} non può essere eliminata perché è utilizzata in Equiro.`}</p>
              {(elig.blockers || []).length > 0 && (
                <ul className="mt-1.5 space-y-0.5" data-testid="party-delete-blockers">
                  {elig.blockers.map((b, i) => (
                    <li key={i} className="text-xs text-[#8C6D4F] flex items-center gap-1.5">
                      <span className="h-1 w-1 rounded-full bg-[#8C6D4F]" /> {b}
                    </li>
                  ))}
                </ul>
              )}
              {onArchive && (
                <button data-testid="party-delete-suggest-archive" onClick={onArchive}
                  className="mt-2 text-xs text-[#8C532B] hover:underline">
                  Puoi archiviarla per non proporla nelle nuove operazioni →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={confirm} onOpenChange={(o) => !o && setConfirm(false)}>
        <DialogContent data-testid="party-delete-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Eliminare definitivamente «{name}»?</DialogTitle>
            <DialogDescription>L'eliminazione definitiva non può essere annullata.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} disabled={busy} data-testid="party-delete-cancel">Annulla</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doDelete} disabled={busy} data-testid="party-delete-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
